import React, { useState } from 'react';
import { FINISHES, Finish } from '../../data/finishes';
import { SCHEDULER_URL } from '../../constants';
import FinishSelector from './FinishSelector';

function Hero() {
  const [selected, setSelected] = useState<Finish['id']>('white');
  const [displayedFinish, setDisplayedFinish] = useState<Finish['id']>('white');
  const [fading, setFading] = useState(false);

  const handleChange = (id: Finish['id']) => {
    if (id === selected) return;
    setSelected(id);
    setFading(true);
    window.setTimeout(() => {
      setDisplayedFinish(id);
      setFading(false);
    }, 180);
  };

  const displayed = FINISHES.find((f) => f.id === displayedFinish) ?? FINISHES[0];

  return (
    <section className="relative pt-[64px] md:pt-[88px] pb-[60px] overflow-hidden">
      <div
        className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_60%_55%_at_78%_42%,rgba(115,199,227,0.18),transparent_70%)]"
        aria-hidden="true"
      />
      <div className="max-w-[1180px] mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-[1.05fr_0.95fr] gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-cream shadow-nu-in text-[0.78rem] font-semibold tracking-[0.08em] uppercase text-navy px-4 py-2 rounded-full mb-[22px]">
            <span className="w-[7px] h-[7px] rounded-full bg-sky animate-nu-pulse-ring motion-reduce:animate-none" />
            Now shipping — Goldie V2
          </div>

          <h1 className="font-display text-[2.4rem] md:text-[3.4rem] leading-[1.05] font-bold text-navy-deep">
            Goldie learns your shower.
            <br /> And notices when <span className="text-sky">something&apos;s off.</span>
          </h1>

          <p className="text-[1.12rem] leading-[1.6] text-navy max-w-[480px] my-[22px] mb-[30px]">
            No cameras. No wearables. No button to press. Goldie knows how you normally
            shower — and when your routine changes, it checks in before anyone has to worry.
          </p>

          <div className="flex flex-wrap gap-4 items-center">
            <a
              href={SCHEDULER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-[30px] py-4 font-semibold text-[0.98rem] text-cream-hi rounded-full bg-gradient-to-br from-navy to-navy-deep shadow-[6px_6px_16px_rgba(28,51,80,0.16),-4px_-4px_12px_rgba(255,255,255,0.95)] transition-transform hover:-translate-y-0.5 outline-none focus-visible:ring-2 focus-visible:ring-sky focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
            >
              See Goldie in Action
            </a>
            <a
              href="#predict"
              className="inline-flex items-center gap-2 px-2 py-4 font-semibold text-[0.95rem] text-navy-deep outline-none focus-visible:ring-2 focus-visible:ring-sky rounded-sm"
            >
              ↓ How Care Check works
            </a>
          </div>

          <FinishSelector
            finishes={FINISHES}
            value={selected}
            onChange={handleChange}
            className="items-center md:items-start mt-7"
          />
        </div>

        <div className="relative flex items-center justify-center h-[380px] md:h-[520px]">
          <div className="absolute -inset-[30px] rounded-full border-2 border-sky/35 animate-nu-ring motion-reduce:animate-none" />
          <div className="relative w-[280px] h-[280px] md:w-[420px] md:h-[420px] rounded-full bg-cream shadow-nu-porthole flex items-center justify-center animate-nu-float motion-reduce:animate-none">
            <div className="absolute inset-[18px] md:inset-[26px] rounded-full shadow-nu-porthole-inset" />
            <img
              src={displayed.image}
              alt={`Goldie shower controller in ${displayed.label} finish`}
              className={`relative w-[78%] h-[78%] object-cover rounded-full shadow-nu-hero-img transition-opacity duration-500 ${
                fading ? 'opacity-0' : 'opacity-100'
              }`}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
